import type { CurrentAgentSessionDto } from "@/api/session";

export const CURRENT_SESSION_QUERY_KEY = ["session", "current"] as const;

const SESSION_STORAGE_KEY = "tickbird.session.current.v1";
const MAX_CACHE_AGE_MS = 8 * 60 * 60 * 1000;

export type SessionCacheScope = {
  /** 同一 OIDC 身份令牌内稳定；换人或重新登录后变化。 */
  key: string;
  expiresAt: number;
};

type CachedCurrentSession = {
  version: 1;
  scopeKey: string;
  cachedAt: number;
  expiresAt: number;
  data: CurrentAgentSessionDto;
};

function readStringClaim(claims: Record<string, unknown>, name: string): string | null {
  const value = claims[name];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function readNumberClaim(claims: Record<string, unknown>, name: string): number | null {
  const value = claims[name];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

/**
 * 将业务会话缓存限定在当前 OIDC 登录/令牌会话内。
 * `iat` 是 JWT 的标准签发时间；`sid` / `auth_time` 让支持它们的 IAM 在续签时保持更清晰的会话边界。
 */
export function createSessionCacheScope(idTokenPayload: unknown, now = Date.now()): SessionCacheScope | null {
  if (!idTokenPayload || typeof idTokenPayload !== "object") {
    return null;
  }

  const claims = idTokenPayload as Record<string, unknown>;
  const subject = readStringClaim(claims, "sub");
  if (!subject) {
    return null;
  }

  const sessionMarker = readStringClaim(claims, "sid") ?? readNumberClaim(claims, "auth_time") ?? "unknown";
  const issuedAt = readNumberClaim(claims, "iat") ?? "unknown";
  const tokenExpiresAt = readNumberClaim(claims, "exp");
  const expiresAt = Math.min(tokenExpiresAt ? tokenExpiresAt * 1000 : Number.POSITIVE_INFINITY, now + MAX_CACHE_AGE_MS);

  if (expiresAt <= now) {
    return null;
  }

  return {
    key: JSON.stringify([subject, sessionMarker, issuedAt]),
    expiresAt,
  };
}

function isCurrentAgentSession(value: unknown): value is CurrentAgentSessionDto {
  if (!value || typeof value !== "object") {
    return false;
  }

  const session = value as Partial<CurrentAgentSessionDto>;
  return (
    typeof session.id === "string" &&
    typeof session.iamSubject === "string" &&
    typeof session.displayName === "string" &&
    (session.email === null || typeof session.email === "string") &&
    (session.avatarUrl === null || typeof session.avatarUrl === "string") &&
    typeof session.status === "number" &&
    Array.isArray(session.globalRoles) &&
    session.globalRoles.every((role) => typeof role === "number") &&
    Array.isArray(session.permissions) &&
    session.permissions.every((permission) => typeof permission === "string")
  );
}

export function readCurrentSessionCache(scope: SessionCacheScope, now = Date.now()): { data: CurrentAgentSessionDto; cachedAt: number } | undefined {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }

    const cached = JSON.parse(raw) as Partial<CachedCurrentSession>;
    const cachedAt = cached.cachedAt;
    const data = cached.data;
    const isUsable =
      cached.version === 1 &&
      cached.scopeKey === scope.key &&
      typeof cachedAt === "number" &&
      Number.isFinite(cachedAt) &&
      cachedAt <= now &&
      now - cachedAt <= MAX_CACHE_AGE_MS &&
      typeof cached.expiresAt === "number" &&
      cached.expiresAt > now &&
      isCurrentAgentSession(data);

    if (!isUsable) {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return undefined;
    }

    return { data, cachedAt };
  } catch {
    // 浏览器禁用 storage 或缓存内容损坏时退回正常网络请求。
    return undefined;
  }
}

export function writeCurrentSessionCache(scope: SessionCacheScope, data: CurrentAgentSessionDto, cachedAt = Date.now()): void {
  if (scope.expiresAt <= cachedAt) {
    return;
  }

  const cached: CachedCurrentSession = {
    version: 1,
    scopeKey: scope.key,
    cachedAt,
    expiresAt: scope.expiresAt,
    data,
  };

  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(cached));
  } catch {
    // Safari 隐私模式或 storage 配额异常不应阻塞登录。
  }
}

export function clearCurrentSessionCache(): void {
  try {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // storage 不可用时本来就没有可复用的缓存。
  }
}
